/**
 * @fileoverview Componente Organismo para la lista de chats en el Inbox.
 * Renderiza la lista de conversaciones, gestiona el menú contextual, scroll paginado y estados de selección.
 * Cumple estrictamente con Atomic Design y separación de capas (Data/Actions/UI).
 *
 * @module InboxChatListMC
 * @category Component - Organism
 * @since 2.0.0
 * @version 3.2.0 - JSDoc completo, typedefs, ejemplos y referencias cruzadas
 */

import React, { memo, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import useInboxChatListToggleUI from '../../hooks/inbox/ui/useInboxChatListToggleUI';
import useInboxChatListUI from '../../hooks/inbox/ui/useInboxChatListUI';
import useInboxChatListData from '../../hooks/inbox/data/useInboxChatListData';
import ConversationItemMC from './ConversationItemMC';
import ContextMenuWrapperMC from '../../components/ContextMenuWrapperMC';
import useInboxChatListContextMenuData from '../../hooks/inbox/data/useInboxChatListContextMenuData';
import useInboxChatListContextMenuActions from '../../hooks/inbox/actions/useInboxChatListContextMenuActions';
import EmptyScreenListInboxMC from '../../components/EmptyScreenListInboxMC';
import LoadingMC from '../../components/LoadingMC';
import usePaginatedScrollUI from '../../hooks/shared/ui/usePaginatedScrollUI';

const InboxChatListMC = memo(({
  hasConversationSelected,
  hasCustomFilters,
  isSelectingConversations,
  loading = false,
  selectedConversations = [],
  toggleConversationSelection,
  toggleSelectingConversations,
  exitSelectingConversations,
  syncConversationSelection,
  loadConversations
}) => {
  /**
   * Hook de traducción para textos vacíos y mensajes.
   */
  const { t } = useTranslation('EmptyScreensMC');

  /**
   * Hook Data Layer para obtener conversaciones y paginación.
   */
  const { customerConversations, pagination } = useInboxChatListData();

  /**
   * Mantiene la selección alineada con la lista visible. Se omite mientras carga porque la
   * primera página vacía la lista antes de traer los datos nuevos.
   */
  useEffect(() => {
    if (loading) return;
    syncConversationSelection?.(customerConversations);
  }, [loading, customerConversations, syncConversationSelection]);

  /**
   * Hook Data Layer para estructura base del menú contextual.
   */
  const getContextMenuItems = useInboxChatListContextMenuData();

  /**
   * Hook UI Layer para estado visual y handlers de selección.
   */
  const { handleSelectConversation, isSelected } = useInboxChatListUI();

  /**
   * Hook Actions Layer para obtener handlers del menú contextual.
   */
  const { getHandlers } = useInboxChatListContextMenuActions({
    toggleSelectingConversations,
    toggleConversationSelection,
    exitSelectingConversations,
    isSelectingConversations,
    selectedConversations
  });

  /**
   * Hook UI Layer para scroll paginado desacoplado.
   */
  const { containerRef, handleScroll } = usePaginatedScrollUI({
    loading,
    pagination,
    onNextPage: loadConversations
  });

  /**
   * Hook UI Layer para alternar selección de conversación.
   */
  const getOnToggle = useInboxChatListToggleUI(toggleConversationSelection);

  /**
   * Memoiza el renderizado de la lista de conversaciones.
   */
  const renderedConversations = useMemo(() => {
    if (customerConversations.length === 0 && !loading) {
      return (
        <div className="tw-flex tw-h-full tw-items-center tw-justify-center">
          <EmptyScreenListInboxMC
            iconName={hasCustomFilters ? 'search-outline' : 'empty-inbox'}
            title={hasCustomFilters ? t('chat_list.no_results_title') : t('chat_list.title')}
            description={hasCustomFilters ? t('chat_list.no_results_description') : t('chat_list.description')}
            iconClassName="!tw-size-[48px]"
            containerClassName="tw-w-full"
            innerClassName="tw-w-full"
          />
        </div>
      );
    }
    return (
      <>
        {customerConversations.map((conversation, index) => {
          const handlers = getHandlers(conversation);
          const contextMenuBase = getContextMenuItems(conversation, handlers.targetConversations);
          const menuItems = contextMenuBase?.map((item) => {
            if (item.separator) return { separator: true };
            if (item.stateId !== undefined) {
              return {
                key: item.key,
                label: item.label,
                onClick: () => handlers.markState(item.stateId)
              };
            }
            return {
              key: item.key,
              label: item.label,
              onClick: handlers[item.key],
              subItems: item.subItems?.map((subItem) => ({
                onClick: handlers[item.key],
                ...subItem
              }))
            };
          });
          return (
            <div key={`${conversation.id}-${conversation.platform}`}>
              <ContextMenuWrapperMC
                key={`menu-${conversation.id}-${conversation.platform}-${hasCustomFilters ? 'filters' : 'nofilters'}`}
                id={`chatlist-menu-${conversation.id}-${conversation.platform}`}
                menuItems={menuItems}
              >
                <ConversationItemMC
                  conversation={conversation}
                  isSelected={isSelected(conversation.id)}
                  onSelect={handleSelectConversation}
                  isSelectingConversations={isSelectingConversations}
                  isChecked={hasConversationSelected(conversation.id)}
                  onToggle={getOnToggle(conversation)}
                />
              </ContextMenuWrapperMC>
              {index < customerConversations.length - 1 && (
                <div className="tw-h-px tw-my-1 tw-bg-gray-200"></div>
              )}
            </div>
          );
        })}
      </>
    );
  }, [
    loading,
    customerConversations,
    isSelected,
    handleSelectConversation,
    isSelectingConversations,
    hasConversationSelected,
    getOnToggle,
    t,
    hasCustomFilters,
    getContextMenuItems,
    getHandlers
  ]);

  return (
    <div className="tw-h-full tw-min-h-0">
      <div className="tw-flex tw-h-full tw-min-h-0 tw-w-full tw-flex-col">
        <LoadingMC loading={loading} />
        <div
          ref={containerRef}
          className="tw-flex-1 tw-h-0 tw-min-h-0 tw-overflow-y-auto tw-px-2"
          onScroll={handleScroll}
        >
          {renderedConversations}
        </div>
      </div>
    </div>
  );
});

/**
 * Nombre de displayName para debug y herramientas de desarrollo.
 */
InboxChatListMC.displayName = 'InboxChatListMC';

export default InboxChatListMC;
